export type EventType = "invite_only" | "mixed" | "open";
export type EventFullStatus = "draft" | "invite_only" | "open" | "closed";
export type EventFormat = "in_person" | "webinar" | "hybrid";
export type RegistrationTrack = "direct" | "partner" | "open";
export type RegistrationStatus =
    | "pending"
    | "approved"
    | "declined"
    | "cancelled";

export interface EventRecord {
    id: string;
    slug: string;
    name: string;
    label: string | null;
    date: string;
    time_start: string;
    time_end: string;
    location: string;
    location_name: string;
    location_address: string;
    capacity: number;
    status: "draft" | "open" | "closed";
    format: EventFormat;
    webinar_url: string | null;
    hero_image_url: string | null;
    hero_overlay_opacity: number;
}

export interface TokenRecord {
    id: string;
    event_id: string;
    partner_id: string | null;
    token: string;
    track: "direct" | "partner";
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    used: boolean;
}

export interface EventPartnerInviteRecord {
    id: string;
    event_id: string;
    partner_id: string;
    token: string;
    quota: number;
    spots_used: number;
    order: number;
    created_at: string;
    updated_at: string;
}

export interface PartnerRecord {
    id: string;
    name: string;
    slug: string;
}

export interface RegistrationByToken {
    registration_token: string;
    event_id: string;
    status: RegistrationStatus;
    first_name?: string;
    email?: string;
}

export interface LearningOutcome {
    id: string;
    order: number;
    body: string;
}

export interface SpeakerEntry {
    id: string;
    order: number;
    organisation: string | null;
    name: string | null;
    role: string | null;
    talk_title: string | null;
    talk_description: string | null;
    photo_url: string | null;
    to_be_announced: boolean;
}

export interface AgendaItem {
    id: string;
    order: number;
    time_start: string;
    time_end: string;
    description: string;
}

export interface ExperienceBullet {
    id: string;
    order: number;
    body: string;
}

export interface PartnerEntry {
    id: string;
    order: number;
    name: string;
    logo_url: string | null;
}

export interface EventFull {
    id: string;
    slug: string;
    label: string | null;
    title: string;
    description: string | null;
    date: string;
    time_start: string;
    time_end: string;
    location_name: string;
    location_address: string;
    status: EventFullStatus;
    type: EventType;
    format: EventFormat;
    webinar_url: string | null;
    capacity: number;
    card_summary: string | null;
    hero_image_url: string | null;
    hero_overlay_opacity: number;
    reminder_after_invite_days: number | null;
    reminder_before_event_days: number | null;
    bg_tension: "white" | "grey";
    bg_why_attend: "white" | "grey";
    bg_program: "white" | "grey";
    bg_experience: "white" | "grey";
    bg_register: "white" | "grey";
    tension_intro: string | null;
    tension_heading: string | null;
    tension_body: string | null;
    why_attend_heading: string | null;
    experience_heading: string | null;
    register_heading: string | null;
    register_note: string | null;
    show_tension: boolean;
    show_why_attend: boolean;
    show_program: boolean;
    show_experience: boolean;
    show_register: boolean;
    show_partners: boolean;
    show_card: boolean;
    learning_outcomes: LearningOutcome[];
    speakers: SpeakerEntry[];
    agenda_items: AgendaItem[];
    experience_bullets: ExperienceBullet[];
    partners: PartnerEntry[];
    created_at: string;
    updated_at: string;
}

type SubmitEventRegistrationInput = {
    eventId: string;
    token?: string | null;
    partnerToken?: string | null;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    jobTitle: string;
    organisation: string;
    motivation: string;
    privacyConsent: boolean;
    marketingConsent: boolean;
};

type SubmitEventRegistrationResult = {
    status: "approved" | "pending";
    registrationToken: string;
};

type CancelEventRegistrationInput = {
    eventId: string;
    registrationToken: string;
};

type CancelEventRegistrationResult = {
    ok: true;
    alreadyCancelled: boolean;
};

type SubmitEventFeedbackInput = {
    eventId: string;
    registrationToken: string;
    feedback: string;
};

type SubmitEventFeedbackResult = {
    ok: true;
};

type UnsubscribeByTokenResult = {
    state: "confirmed" | "already" | "invalid";
};

async function postEventAction<TResponse>(body: Record<string, unknown>): Promise<TResponse> {
    const response = await fetch("/api/event-registration", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
    });

    const payload = (await response.json().catch(() => null)) as
        | { error?: string }
        | null;

    if (!response.ok) {
        throw new Error(payload?.error || "Something went wrong. Please try again.");
    }

    return payload as TResponse;
}

export function getEventBySlug(slug: string): Promise<EventRecord | null> {
    return postEventAction<EventRecord | null>({
        action: "get-event-by-slug",
        slug,
    });
}

export function getEventFullBySlug(slug: string): Promise<EventFull | null> {
    return postEventAction<EventFull | null>({
        action: "get-event-full-by-slug",
        slug,
    });
}

export function listOpenEvents(): Promise<EventFull[]> {
    return postEventAction<EventFull[]>({
        action: "list-open-events",
    });
}

export function findRegistrationByToken(
    eventId: string,
    registrationToken: string,
): Promise<RegistrationByToken | null> {
    return postEventAction<RegistrationByToken | null>({
        action: "find-registration-by-token",
        eventId,
        registrationToken,
    });
}

export function isEventAtCapacity(eventId: string): Promise<boolean> {
    return postEventAction<boolean>({
        action: "is-event-at-capacity",
        eventId,
    });
}

export function lookupToken(
    eventId: string,
    token: string,
): Promise<
    | { ok: true; token: TokenRecord }
    | { ok: false; reason: "not_found" | "wrong_event" | "used" }
> {
    return postEventAction<
        | { ok: true; token: TokenRecord }
        | { ok: false; reason: "not_found" | "wrong_event" | "used" }
    >({
        action: "lookup-token",
        eventId,
        token,
    });
}

export function lookupPartnerInviteByToken(
    eventId: string,
    token: string,
): Promise<EventPartnerInviteRecord | null> {
    return postEventAction<EventPartnerInviteRecord | null>({
        action: "lookup-partner-invite",
        eventId,
        token,
    });
}

export function getPartnerById(id: string | null): Promise<PartnerRecord | null> {
    if (!id) {
        return Promise.resolve(null);
    }

    return postEventAction<PartnerRecord | null>({
        action: "get-partner-by-id",
        id,
    });
}

export function submitEventRegistration(
    input: SubmitEventRegistrationInput,
): Promise<SubmitEventRegistrationResult> {
    return postEventAction<SubmitEventRegistrationResult>({
        action: "submit-registration",
        ...input,
    });
}

export function cancelEventRegistration(
    input: CancelEventRegistrationInput,
): Promise<CancelEventRegistrationResult> {
    return postEventAction<CancelEventRegistrationResult>({
        action: "cancel-registration",
        ...input,
    });
}

export function submitEventFeedback(
    input: SubmitEventFeedbackInput,
): Promise<SubmitEventFeedbackResult> {
    return postEventAction<SubmitEventFeedbackResult>({
        action: "submit-feedback",
        ...input,
    });
}

export function unsubscribeByToken(token: string): Promise<UnsubscribeByTokenResult> {
    return postEventAction<UnsubscribeByTokenResult>({
        action: "unsubscribe-by-token",
        token,
    });
}